import type { ReactNode } from "react";
import { useRouteMatch } from "react-router-dom";

import { get } from "lodash-es";

import { useTheme } from "@smartrent/ui";

import AppShellBase from "@/components/AppShellBase";
import { StateType, useAppState } from "@/context/AppState";
import { useDocumentTitle } from "@/hooks/document-title";
import { clearStorage, clearStorageStayLoggedIn } from "@/utils/storage";

type Props = {
  header?: {
    title?: null | ReactNode;
    left?: null | string;
    right?: null | ReactNode;
  };
  showNewTourButton?: boolean;
  showPhoneNumber?: boolean;
  title?: string; //used to set the document title
  children: ReactNode;
};

export default function AppShellContainer({
  header = {
    title: null,
    left: "Back",
    right: null,
  },
  showNewTourButton = true,
  showPhoneNumber = true,
  title,
  children,
}: Props) {
  const [appState, setAppState] = useAppState();
  const { config, setAccessTokens } = appState;
  const route = useRouteMatch();
  useDocumentTitle(title);

  const { colors, shadows } = useTheme();

  // If the user is already has an access token from phone verification and the tour has ended or the tour is ongoing
  // hitting start over will lead them back to the unit selection so they don't have to auth again.
  const handleReset = async () => {
    const isTourPage = route && route.path === "/tour/:tourToken";
    const isTourEndPage =
      route && route.path === "/tour/:tourToken/end-of-tour";
    if (isTourPage || isTourEndPage) {
      await clearStorageStayLoggedIn();
    } else {
      await clearStorage();
      await setAppState({
        ...appState,
        tourRescheduled: false,
      });
      setAccessTokens({}); // clear reference to access tokens given that we just cleared them all locally
    }

    window.location.href = `/property/${config.uuid}`;

    // Remove tour from state, to ensure they get reloaded
    // properly at the correct time. (This is a dependency of the tour page.)
    setAppState((state: StateType) => ({
      ...state,
      tour: undefined,
    }));

    if (!isTourPage) {
      // Don't do this if they're on the Tour page, because they'd be clicking New Tour,
      // which keeps them logged in. If we clear prospect in that scenario, then
      // when they get to the contact details page, the form won't pre-fill.
      setAppState((state: StateType) => ({
        ...state,
        prospect: undefined,
      }));
    }
  };

  return (
    <AppShellBase
      header={header}
      route={route}
      handleReset={handleReset}
      colors={colors}
      shadows={shadows}
      showNewTourButton={showNewTourButton}
      propertyName={(config && config.propertyName) || ""}
      phoneNumber={showPhoneNumber ? get(config, "phoneNumber") : null}
    >
      {children}
    </AppShellBase>
  );
}
